import { differenceInMinutes } from 'date-fns';
import { getStorage, setStorage } from './storage.adapter';
import { env } from './env';
import { CONTEXT, getContext } from './context';

const supabaseUrl = env('VITE_SUPABASE_URL');
const supabaseAnonKey = env('VITE_SUPABASE_ANON_KEY');

interface TokenData {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user?: any;
}

let refreshPromise: Promise<string | null> | null = null;

export async function refreshAccessToken(refreshToken?: string): Promise<string | null> {
  if (refreshPromise) return await refreshPromise;

  const authState = await getAuthState();
  const token = refreshToken || authState?.refresh_token || '';

  if (!token) throw new Error('No refresh token available');

  console.log('supabaseUrl', supabaseUrl, supabaseAnonKey);

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=refresh_token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
        },
        body: JSON.stringify({ refresh_token: token }),
      });

      if (!res.ok) {
        console.error('[auth] refreshAccessToken: Failed with status:', res.status);
        return null;
      }

      const data = await res.json();

      const tokenData: TokenData = JSON.parse(
        JSON.stringify({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at || 0,
          user: data.user,
        })
      );
      await storeTokens(tokenData);
      return tokenData.access_token;
    } catch (e) {
      console.error('[auth] refreshAccessToken: Exception during refresh:', e);
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return await refreshPromise;
}

function isTokenExpired(expiresAt: number): boolean {
  console.log('expiresAt => ', expiresAt);
  const now = Date.now();
  const expiration = new Date(expiresAt * 1000);
  const diff = differenceInMinutes(now, expiration);
  console.log('diff => ', diff, diff >= -3);
  return diff >= -3;
}

async function getAuthState(): Promise<TokenData | null> {
  if (getContext() === CONTEXT.PWA) {
    const storage = await getStorage<'pwa'>('local', ['auth']);
    return storage?.auth ?? null;
  }
  const storage = await getStorage('sync', ['auth']);
  return storage?.auth ?? null;
}

export async function storeTokens(tokenData: TokenData): Promise<void> {
  if (getContext() === CONTEXT.PWA) {
    await setStorage<'pwa'>('local', { auth: tokenData });
  } else {
    await setStorage('sync', { auth: tokenData });
  }
}

export async function getValidToken(): Promise<string | null> {
  console.log('[auth] getValidToken: getting valid token');
  const stored = await getAuthState();
  console.log('[auth] getValidToken: stored => ', stored);
  if (!stored) {
    return null;
  }

  if (!isTokenExpired(stored.expires_at)) {
    return stored.access_token;
  }

  console.log('[auth] getValidToken: token expired, refreshing...');

  const newToken = await refreshAccessToken(stored.refresh_token);
  if (!newToken) {
    if (getContext() === CONTEXT.PWA) {
      await setStorage<'pwa'>('local', { auth: null });
    } else {
      await setStorage('sync', { auth: null });
    }
    return null;
  }
  console.log('[auth] getValidToken: token refreshed successfully', newToken);
  return newToken;
}

export const authenticatedFetch = async (
  url: string,
  options: Omit<RequestInit, 'body'> & { body?: any } = {}
) => {
  try {
    console.log('[auth] authenticatedFetch: getting valid token');
    const validToken = await getValidToken();

    if (!validToken) {
      console.error('[auth] authenticatedFetch: No valid token available');
      return null;
    }

    const isFormData = options.body instanceof FormData;

    const headers = new Headers(options.headers);
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }
    headers.set('Authorization', `Bearer ${validToken}`);
    headers.set('X-Client-Timestamp', new Date().toISOString());

    if (!isFormData) {
      headers.set('Content-Type', 'application/json');
    }

    const res = await fetch(url, {
      method: options.method || 'GET',
      headers,
      body: isFormData
        ? options.body
        : options.body
          ? typeof options.body === 'string'
            ? options.body
            : JSON.stringify(options.body)
          : undefined,
    });

    return res;
  } catch (error) {
    console.error('[auth] authenticatedFetch failed:', error);
    return null;
  }
};
